/**
 * Official brand marks for catalog platforms, keyed by catalog platform id.
 *
 * Data only: `BrandIcon` renders these and every other surface reads them
 * through it. simple-icons entries satisfy `BrandMark` as-is; the inline marks
 * below are the platforms simple-icons does not carry, or dropped on a
 * trademark request, each taken from that platform's own official artwork.
 */
import {
  siGithub,
  siReddit,
  siTiktok,
  siInstagram,
  siYoutube,
  siX,
  siFacebook,
  siGoogle,
  siThreads,
  siSubstack,
  siPinterest,
  siSnapchat,
  siSpotify,
  siEbay,
  siEtsy,
  siShopify,
  siAlibabadotcom,
  siFiverr,
  siTrustpilot,
  siG2,
  siSemrush,
  siPexels,
  siUnsplash,
  siSimilarweb,
  siAppstore,
  siGoogleplay,
  siGoogleads,
  siGooglemaps,
  siYelp,
  siCrunchbase,
  siIndeed,
  siGlassdoor,
  siUpwork,
  siZillow,
  siBookingdotcom,
  siAirbnb,
  siTripadvisor,
  siCoinmarketcap,
  siTwitch,
  siBluesky,
  siYcombinator,
  siSinaweibo,
  siZhihu,
  siXiaohongshu,
  siNaver,
  siGooglegemini,
  siPerplexity,
} from 'simple-icons';

/** The minimal brand-mark shape we render: official logo path + brand color. */
export interface BrandMark {
  title: string;
  /** brand hex without the leading # */
  hex: string;
  /** SVG path data interpreted in `viewBox`, which defaults to `0 0 24 24` */
  path: string;
  /** optional tile rendered behind the main mark */
  backgroundPath?: string;
  /** fixed main-mark color for two-color app icons */
  pathHex?: string;
  /** overrides the default `0 0 24 24` box when the mark is authored elsewhere */
  viewBox?: string;
  /** `evenodd` for marks that knock a shape out of a filled tile */
  fillRule?: 'evenodd';
}

/** LinkedIn was removed from simple-icons (trademark request), so its official
 *  mark is supplied inline here rather than imported. */
const LINKEDIN: BrandMark = {
  title: 'LinkedIn',
  hex: '0A66C2',
  path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
};

/** Amazon and Walmart were removed from simple-icons (trademark requests); their
 *  official marks are supplied inline. */
const AMAZON: BrandMark = {
  title: 'Amazon',
  hex: 'FF9900',
  path: 'M.045 18.02c.072-.116.187-.124.348-.022 3.636 2.11 7.594 3.166 11.87 3.166 2.852 0 5.668-.533 8.447-1.595l.315-.14c.138-.06.234-.1.293-.13.226-.088.39-.046.525.13.12.174.09.336-.12.48-.256.19-.6.41-1.006.654-1.244.743-2.64 1.316-4.185 1.726a17.617 17.617 0 01-10.951-.577 17.88 17.88 0 01-5.43-3.35c-.1-.074-.151-.15-.151-.22 0-.047.021-.09.051-.13zm6.565-6.218c0-1.005.247-1.863.743-2.577.495-.71 1.17-1.25 2.04-1.615.796-.335 1.756-.575 2.912-.72.39-.046 1.033-.103 1.92-.174v-.37c0-.93-.105-1.558-.3-1.875-.302-.43-.78-.65-1.44-.65h-.182c-.48.046-.896.196-1.246.46-.35.27-.575.63-.675 1.096-.06.3-.206.465-.435.51l-2.52-.315c-.248-.06-.372-.18-.372-.39 0-.046.007-.09.022-.15.247-1.29.855-2.25 1.82-2.88.976-.616 2.1-.975 3.39-1.05h.54c1.65 0 2.957.434 3.888 1.29.135.15.27.3.405.48.12.165.224.314.283.45.075.134.15.33.195.57.06.254.105.42.135.51.03.104.062.3.076.615.01.313.02.493.02.553v5.28c0 .376.06.72.165 1.036.105.313.21.54.315.674l.51.674c.09.136.136.256.136.36 0 .12-.06.226-.18.314-1.2 1.05-1.86 1.62-1.963 1.71-.165.135-.375.15-.63.045a6.062 6.062 0 01-.526-.496l-.31-.347a9.391 9.391 0 01-.317-.42l-.3-.435c-.81.886-1.603 1.44-2.4 1.665-.494.15-1.093.227-1.83.227-1.11 0-2.04-.343-2.76-1.034-.72-.69-1.08-1.665-1.08-2.94l-.05-.076zm3.753-.438c0 .566.14 1.02.425 1.364.285.34.675.512 1.155.512.045 0 .106-.007.195-.02.09-.016.134-.023.166-.023.614-.16 1.08-.553 1.424-1.178.165-.28.285-.58.36-.91.09-.32.12-.59.135-.8.015-.195.015-.54.015-1.005v-.54c-.84 0-1.484.06-1.92.18-1.275.36-1.92 1.17-1.92 2.43l-.035-.02zm9.162 7.027c.03-.06.075-.11.132-.17.362-.243.714-.41 1.05-.5a8.094 8.094 0 011.612-.24c.14-.012.28 0 .41.03.65.06 1.05.168 1.172.33.063.09.099.228.099.39v.15c0 .51-.149 1.11-.424 1.8-.278.69-.664 1.248-1.156 1.68-.073.06-.14.09-.197.09-.03 0-.06 0-.09-.012-.09-.044-.107-.12-.064-.24.54-1.26.806-2.143.806-2.64 0-.15-.03-.27-.087-.344-.145-.166-.55-.257-1.224-.257-.243 0-.533.016-.87.046-.363.045-.7.09-1 .135-.09 0-.148-.014-.18-.044-.03-.03-.036-.047-.02-.077 0-.017.006-.03.02-.063v-.06z',
};

const WALMART: BrandMark = {
  title: 'Walmart',
  hex: '0071CE',
  path: 'M21.41818 9.10219c-.22048 0-.39583.12308-.39583.27297l.13393 1.51627c.01478.09132.12669.16185.26197.16185.13555-.00017.24705-.07065.26214-.16185l.13424-1.51627c0-.1499-.17555-.27297-.39645-.27297zM-.00002 10.3184s.59713 2.44699.69242 2.84417c.11123.46362.3117.63419.88954.51913l.37291-1.51718c.0945-.37683.1579-.64553.21866-1.02883h.01065c.04269.3871.10354.65314.18131 1.03017 0 0 .15176.68869.22949 1.05042.07795.36163.29482.5895.86083.46542l.88851-3.3633h-.71735l-.30339 1.45411c-.08155.42325-.15544.75396-.21251 1.14117h-.01022c-.05189-.38347-.11777-.70096-.20072-1.11331l-.31586-1.48197h-.7474l-.3378 1.44462c-.09569.43899-.18528.79337-.2422 1.16745h-.01023c-.05832-.35224-.13599-.7977-.22006-1.22261 0 0-.20074-1.03328-.27115-1.38946zm6.83845 0v3.3633h.68299v-3.3633zm9.6188 0v2.48118c0 .34202.0644.5817.20213.72811.12033.12806.31854.21094.55604.21094.20193 0 .40062-.0383.49426-.07317l-.0088-.53367c-.06968.01711-.1498.03078-.25942.03078-.23265 0-.31068-.149-.31068-.45611v-.94921h.59479v-.64351h-.59481v-.79533zm2.77885 0c-.11446.0027-.24452.08936-.32723.23277-.11062.19096-.09105.40434.03838.47923l1.3799.64254c.0862.03205.20323-.02912.27103-.14597.06814-.11745.0629-.2496-.0088-.3082l-1.24635-.8741c-.03237-.01874-.06877-.02717-.10693-.02627zm4.36427 0c-.03815-.0009-.0745.0075-.1068.02628l-1.2464.8741c-.07112.05846-.07653.1901-.0092.30734.00006.00013.00015.00023.00025.00036.00009.00016.00015.00033.00024.00049.06804.11686.18472.17803.27091.14598l1.38004-.64254c.12997-.0749.14861-.28827.03874-.47923-.08309-.1434-.21333-.23006-.32777-.23277zM5.312 11.0981c-.42444 0-.76136.11916-.94501.22529l.13442.46019c.16808-.10595.43566-.19366.68907-.19366.41954-.0011.48817.23728.48817.39012v.03613c-.9142-.0014-1.49164.31493-1.49164.9598 0 .3937.29399.76266.80512.76266.31466 0 .57778-.12554.73548-.32662h.01545s.10445.4367.67982.26969c-.03022-.18174-.04002-.37546-.04002-.60884v-.89849c0-.57263-.24452-1.07627-1.07086-1.07627zm4.08552 0c-.42739 0-.61944.2166-.7359.40034h-.01016v-.34335h-.65173v2.5266h.68658V12.2c0-.06945.00799-.1429.03223-.2068.05689-.1492.19565-.3237.41725-.3237.27704 0 .40667.2342.40667.57222v1.44h.68585v-1.4996c0-.06636.0091-.14622.02859-.20486.05639-.16969.20602-.30776.41201-.30776.28086 0 .41567.23012.41567.62788v1.38434h.68633v-1.48805c0-.78478-.39845-1.09555-.8483-1.09555-.19922 0-.35646.04996-.49863.13722-.1195.07334-.22655.17753-.32006.3147h-.0101c-.10853-.27228-.36375-.45192-.6963-.45192zm3.7702 0c-.42435 0-.76113.11916-.94495.22529l.13454.46019c.16792-.10595.43572-.19366.689-.19366.41926-.0011.48806.23728.48806.39012v.03613c-.91407-.0014-1.49164.31494-1.49164.9598 0 .3937.29418.76266.8056.76266.31441 0 .57759-.12554.735-.32662h.01557s.10437.4367.67982.26969c-.03027-.18174-.03996-.37546-.03996-.60884v-.89849c0-.57263-.24458-1.07627-1.07104-1.07627zm2.85129 0c-.26292 0-.56205.1697-.68761.53354h-.0191v-.47655h-.6181v2.5266h.70453V12.388c0-.06985.0042-.1307.01527-.1865.0521-.27102.25945-.44425.55696-.44425.08167 0 .1401.0088.20333.018v-.66151c-.05302-.0107-.0893-.01563-.15528-.01563zm4.35946 1.22067c-.01785-.00025-.03513.0026-.05134.0087l-1.3799.6418c-.12943.07519-.149.28868-.03838.47984.11028.1906.30469.28118.43415.20644l1.24634-.87349c.0717-.05929.07696-.19127.0088-.30862l.0006.00025c-.05507-.09558-.14292-.15388-.22027-.15492zm2.07955 0c-.07727.001-.1649.05934-.22012.15491l.00049-.00025c-.06781.11735-.06254.24934.0088.30862l1.2464.87349c.12921.07474.3238-.01584.43458-.20644.10986-.19116.09122-.40466-.03875-.47983l-1.38012-.64181c-.0162-.0061-.03344-.0089-.05128-.0087zm-16.75741.14518v.31519c0 .0466-.00406.09467-.01697.13673-.05286.17506-.23415.32303-.46086.32303-.18901 0-.33916-.1073-.33916-.33422 0-.34707.38204-.443.81699-.44073zm7.85577 0v.31519c0 .0466-.0041.09467-.0169.13673-.05287.17506-.23421.32303-.46093.32303-.18905 0-.3392-.1073-.3392-.33422 0-.34707.38209-.443.81703-.44073zm7.86138.48324c-.13506.00016-.24672.07024-.26148.16137l-.13393 1.5162c0 .15015.17535.27304.39583.27304.2209 0 .39645-.12289.39645-.27303l-.13424-1.51621c-.01509-.09113-.12659-.1612-.26214-.16137z',
};

/** Yahoo was removed from simple-icons (trademark request); this is its
 *  official mark from the last release that carried it (v9). */
const YAHOO: BrandMark = {
  title: 'Yahoo',
  hex: '6001D2',
  path: 'M18.86 1.56L14.27 11.87H19.4L24 1.56H18.86M0 6.71L5.15 18.27L3.3 22.44H7.83L14.69 6.71H10.19L7.39 13.44L4.62 6.71H0M15.62 12.87C13.95 12.87 12.71 14.12 12.71 15.58C12.71 17 13.91 18.19 15.5 18.19C17.18 18.19 18.43 16.96 18.43 15.5C18.43 14.03 17.23 12.87 15.62 12.87Z',
};

/** Ahrefs has no simple-icons entry; this is the official favicon mark
 *  (ahrefs.com/favicon.svg) rescaled from its 429×512 viewBox to 24×24. */
const AHREFS: BrandMark = {
  title: 'Ahrefs',
  hex: 'FF8D00',
  path: 'M4.178 0V4.465L16.178 4.455V9.488L9.504 9.498C4.683 9.498 1.945 12.025 1.945 16.649C1.945 21.272 4.683 24 9.504 24H9.851C12.512 24 14.559 22.93 16.178 21.35V23.442H22.038V0H4.178ZM11.355 19.654C8.886 19.654 7.768 18.691 7.768 16.649C7.768 14.606 8.887 13.719 11.355 13.681H16.214C16.214 17.034 14.672 19.462 11.355 19.654Z',
};

/** RedNote's recognizable mark is the app tile, not the bare wordmark that
 *  simple-icons ships - three 8.6-unit-tall characters read as a smear at chip
 *  size. So we prepend a rounded-square tile sized around the wordmark's own
 *  coordinates and knock the characters out of it with `evenodd`, then widen the
 *  viewBox to the tile to match the official red app-icon treatment. */
const REDNOTE: BrandMark = {
  title: 'RedNote',
  hex: siXiaohongshu.hex,
  path:
    'M2.255 -5.396H21.735a7.66 7.66 0 0 1 7.66 7.66V21.744a7.66 7.66 0 0 1 -7.66 7.66H2.255a7.66 7.66 0 0 1 -7.66 -7.66V2.264a7.66 7.66 0 0 1 7.66 -7.66Z' +
    siXiaohongshu.path,
  viewBox: '-6.449 -6.44 36.888 36.888',
  fillRule: 'evenodd',
};

/** OpenAI has no simple-icons entry (it ships only `openaigym`). This is the
 *  official mark from chatgpt.com's own favicon, already authored in a 24x24
 *  box, so it needs no rescaling. */
const CHATGPT: BrandMark = {
  title: 'ChatGPT',
  hex: '000000',
  path: 'M9.90382 9.49261V7.7981C9.90382 7.65538 9.95737 7.54831 10.0822 7.47703L13.4896 5.515C13.9534 5.24747 14.5065 5.12266 15.0773 5.12266C17.2179 5.12266 18.5738 6.78153 18.5738 8.54731C18.5738 8.67212 18.5738 8.81484 18.5559 8.95756L15.0237 6.88844C14.8097 6.76363 14.5955 6.76363 14.3815 6.88844L9.90382 9.49261ZM17.8602 16.0923V12.0432C17.8602 11.7934 17.7531 11.6151 17.5391 11.4903L13.0614 8.88611L14.5242 8.04772C14.649 7.97645 14.7561 7.97645 14.881 8.04772L18.2884 10.0098C19.2696 10.5806 19.9296 11.7934 19.9296 12.9706C19.9296 14.3262 19.1269 15.5748 17.8602 16.0921V16.0923ZM8.8513 12.5249L7.38847 11.6688C7.26364 11.5975 7.2101 11.4905 7.2101 11.3477V7.42366C7.2101 5.51517 8.67292 4.07028 10.6531 4.07028C11.4025 4.07028 12.0981 4.32007 12.6869 4.76595L9.17258 6.79943C8.95856 6.92425 8.85147 7.1026 8.85147 7.35239V12.5251L8.8513 12.5249ZM12 14.3442L9.90382 13.1671V10.67L12 9.49278L14.096 10.67V13.1671L12 14.3442ZM13.3469 19.7667C12.5975 19.7667 11.9019 19.517 11.3131 19.0711L14.8274 17.0376C15.0414 16.9128 15.1485 16.7344 15.1485 16.4846V11.3119L16.6293 12.1681C16.7541 12.2393 16.8076 12.3464 16.8076 12.4891V16.4132C16.8076 18.3217 15.3269 19.7666 13.3469 19.7666V19.7667ZM9.11886 15.7891L5.71146 13.8271C4.73022 13.2562 4.07026 12.0434 4.07026 10.8662C4.07026 9.49278 4.89086 8.26205 6.1574 7.74473V11.8115C6.1574 12.0613 6.2645 12.2397 6.47851 12.3645L10.9384 14.9507L9.47561 15.7891C9.35078 15.8604 9.24369 15.8604 9.11886 15.7891ZM8.92275 18.7144C6.90689 18.7144 5.42616 17.1982 5.42616 15.3254C5.42616 15.1826 5.44407 15.0399 5.4618 14.8972L8.97613 16.9307C9.19014 17.0555 9.40433 17.0555 9.61835 16.9307L14.096 14.3444V16.0389C14.096 16.1816 14.0425 16.2887 13.9176 16.36L10.5102 18.322C10.0464 18.5896 9.49335 18.7144 8.92258 18.7144H8.92275ZM13.3469 20.8369C15.5055 20.8369 17.3071 19.303 17.7176 17.2695C19.7156 16.7522 21 14.8793 21 12.9708C21 11.7222 20.4649 10.5093 19.5015 9.63532C19.5907 9.26072 19.6443 8.88611 19.6443 8.51168C19.6443 5.96104 17.5749 4.05238 15.1843 4.05238C14.7028 4.05238 14.2389 4.12365 13.7751 4.28427C12.9722 3.49942 11.8661 3 10.6531 3C8.49454 3 6.69287 4.53389 6.2824 6.56737C4.28445 7.08469 3 8.95755 3 10.8661C3 12.1147 3.53513 13.3275 4.49847 14.2015C4.40928 14.5761 4.35573 14.9507 4.35573 15.3252C4.35573 17.8758 6.42514 19.7845 8.81565 19.7845C9.29724 19.7845 9.76108 19.7132 10.2249 19.5526C11.0276 20.3374 12.1337 20.8369 13.3469 20.8369Z',
};

/** Truth Social ships no vector logo, so this mark is redrawn on the grid of
 *  its own app icon (truthsocial.com/apple-touch-icon.png): a detached square,
 *  the blocky "T", and the teal accent square. The purple elements sit in
 *  `backgroundPath` so they take the brand color, leaving the teal square as
 *  the fixed-color main path. */
const TRUTH_SOCIAL: BrandMark = {
  title: 'Truth Social',
  hex: '5448EE',
  path: 'M17.806 16.64H24v5.43h-6.194z',
  pathHex: '2DD0B3',
  backgroundPath: 'M0 1.901h6.19v5.43H0zM24 7.331V1.901H8.903V22.07h6.19V7.331z',
};

/** simple-icons ships Nextdoor's full "nextdoor" wordmark, which reads as a
 *  smear in a 34px chip like RedNote's does. This is the official app icon
 *  instead (nextdoor.com/apple-touch-icon-180x180.png and its favicon.svg): the
 *  green tile in `backgroundPath` so it takes the brand color, and the white
 *  house rescaled from the source 180x180 box as the fixed-color main path. */
const NEXTDOOR: BrandMark = {
  title: 'Nextdoor',
  hex: '1B8751',
  path: 'M9.493 8.015V6.432H7.435V9.308L4.995 10.841L6.09 12.585L7.441 11.736V17.571H16.587V11.736L17.937 12.585L19.033 10.841L12.014 6.432L9.493 8.015Z',
  pathHex: 'F0F2F5',
  backgroundPath: 'M5 0H19A5 5 0 0 1 24 5V19A5 5 0 0 1 19 24H5A5 5 0 0 1 0 19V5A5 5 0 0 1 5 0Z',
};

/** Redfin has no simple-icons entry; this is the "R" symbol taken from the
 *  official inline logo on redfin.com, rescaled from its 116x23 box. */
const REDFIN: BrandMark = {
  title: 'Redfin',
  hex: 'DE3341',
  path: 'M24 12.83c0 4.38-2.34 8.22-5.84 10.32a.3.3 0 0 1-.41-.06l-4.03-4.3a.33.33 0 0 1 .13-.54 5.7 5.7 0 0 0 3.87-5.42A5.7 5.7 0 0 0 11.9 7.1a5.8 5.8 0 0 0-5.62 5.82v9.9a.33.33 0 0 1-.5.29A12 12 0 0 1 0 12.98 12.05 12.05 0 0 1 11.83.81 12 12 0 0 1 24 12.83',
};

/** Realtor.com has no simple-icons entry; this is the house symbol from the
 *  official inline logo on realtor.com, rescaled from its 227x32 box. The
 *  lowercase "r" is knocked out of the house with `evenodd`, as in the
 *  original. */
const REALTOR: BrandMark = {
  title: 'Realtor.com',
  hex: 'D92228',
  path: 'M24 12.63 12.01.33 0 12.63h3.66v11.04h16.68V12.63zm-7.41-3.22a2 2 0 0 0-1.44-.5c-1 0-1.63.43-2.07.79a5 5 0 0 0-1.35 2.1l.02-.88v-.25l.02-1.2c0-.14-.09-.28-.3-.28h-3.9v.33h.1c.32.03.62.06.9.26q.38.25.44.69c.08.34.08 1.33.08 1.76v5.65c0 .58-.01 1.37-.1 1.9-.16.51-.33.67-.67.84q-.28.13-.7.19h-.1v.33h5.82v-.33h-.1a2 2 0 0 1-.65-.16q-.25-.09-.48-.52a5 5 0 0 1-.38-2.38v-2.36c0-.71.04-1.4.06-1.7.26-1.88 1.28-3.39 1.95-3.39.25 0 .42.28.6.72.21.5.51 1.04 1.38 1.04.85 0 1.3-.72 1.3-1.44q.01-.73-.43-1.21',
  fillRule: 'evenodd',
};

/** Polymarket has no simple-icons entry; this is its official symbol from
 *  Wikimedia Commons (`Polymarket.svg`), rescaled from its 911x168 box. */
const POLYMARKET: BrandMark = {
  title: 'Polymarket',
  hex: '1652F0',
  path: 'M21.96 22c0 1.07 0 1.6-.34 1.86s-.86.12-1.88-.17L3.32 19.1c-.62-.18-.93-.26-1.1-.5s-.18-.55-.18-1.2V6.6c0-.64 0-.96.18-1.2s.48-.31 1.1-.49L19.74.31c1.02-.3 1.53-.43 1.88-.17s.34.8.34 1.85zM6.13 17.59l13.6 3.82v-7.63zm-1.86-1.77L17.87 12 4.27 8.19zm1.86-9.4 13.6 3.82V2.6z',
};

/** Catalog platform id → official brand mark. Keyed by the catalog card `id`
 *  (the detail-page route segment), so `x-twitter` maps to the X logo.
 *  simple-icons entries satisfy `BrandMark` as-is (title, hex, path). */
export const BRANDS: Record<string, BrandMark> = {
  ahrefs: AHREFS,
  semrush: siSemrush,
  pexels: siPexels,
  unsplash: siUnsplash,
  similarweb: siSimilarweb,
  github: siGithub,
  'yahoo-finance': YAHOO,
  reddit: siReddit,
  tiktok: siTiktok,
  douyin: { ...siTiktok, title: 'Douyin' },
  weibo: siSinaweibo,
  zhihu: siZhihu,
  rednote: REDNOTE,
  'tiktok-shop': siTiktok,
  instagram: siInstagram,
  youtube: siYoutube,
  'x-twitter': siX,
  facebook: siFacebook,
  linkedin: LINKEDIN,
  google: siGoogle,
  threads: siThreads,
  substack: siSubstack,
  pinterest: siPinterest,
  snapchat: siSnapchat,
  spotify: siSpotify,
  amazon: AMAZON,
  walmart: WALMART,
  ebay: siEbay,
  etsy: siEtsy,
  shopify: siShopify,
  alibaba: siAlibabadotcom,
  fiverr: siFiverr,
  trustpilot: siTrustpilot,
  g2: siG2,
  appstore: siAppstore,
  playstore: siGoogleplay,
  'google-ads': siGoogleads,
  maps: siGooglemaps,
  yelp: siYelp,
  crunchbase: siCrunchbase,
  indeed: siIndeed,
  glassdoor: siGlassdoor,
  upwork: siUpwork,
  zillow: siZillow,
  booking: siBookingdotcom,
  airbnb: siAirbnb,
  tripadvisor: siTripadvisor,
  coinmarketcap: siCoinmarketcap,
  twitch: siTwitch,
  bluesky: siBluesky,
  hackernews: siYcombinator,
  naver: siNaver,
  nextdoor: NEXTDOOR,
  chatgpt: CHATGPT,
  truthsocial: TRUTH_SOCIAL,
  redfin: REDFIN,
  realtor: REALTOR,
  polymarket: POLYMARKET,
  gemini: siGooglegemini,
  perplexity: siPerplexity,
};

/** Google's official multicolor "G" (the sign-in mark) - simple-icons only
 *  ships a flat single-color G, which reads as off-brand. Reused for Google
 *  sub-brands without a distinct mark in simple-icons. */
export const GOOGLE_G: { fill: string; d: string }[] = [
  { fill: '#4285F4', d: 'M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z' },
  { fill: '#34A853', d: 'M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z' },
  { fill: '#FBBC05', d: 'M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z' },
  { fill: '#EA4335', d: 'M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z' },
];

/** Google sub-brand catalog ids that render the multicolor Google G. */
export const GOOGLE_FAMILY = new Set(['google-shopping', 'google-finance']);

/** A brand whose official mark we ship as an image file rather than a path. */
export interface RasterBrandMark {
  title: string;
  /** brand hex without the leading `#`, used for the chip tint */
  hex: string;
  /** public asset path */
  src: string;
}

/**
 * Brands with no usable vector source. Capterra has no simple-icons entry and
 * Gartner's WAF blocks the site, its apple-touch-icon, and every subdomain that
 * might carry an SVG; the regional domains do serve a 64px PNG but it is fully
 * transparent. So the mark is cloned from the 48px frame of
 * `capterra.com/favicon.ico`. Tracing it by hand was tried first and rejected:
 * at 48px the derived polygons read visibly squatter than the real mark.
 *
 * Icypeas and Allegrow are the same case: neither has a simple-icons entry, and
 * both publish only a raster app icon. Each is a customer-facing catalog name
 * (`email_finding.icypeas`, `email_verification.allegrow`), not a routing lane.
 * The hex on each is the accent sampled off its own artwork, which is all the
 * chip tint uses.
 *
 * Google Hotels is a multicolor vector rather than a raster: it lives inside
 * Google Travel, whose suitcase mark is too many colored shapes for one path, so
 * it ships as an SVG file (Wikimedia's Google_Travel_logo.svg, its viewBox
 * squared around the artwork's centre).
 */
export const RASTER_BRANDS: Record<string, RasterBrandMark> = {
  capterra: { title: 'Capterra', hex: 'F89B2D', src: '/brand-marks/capterra.png' },
  icypeas: { title: 'Icypeas', hex: '02C29F', src: '/brand-marks/icypeas.png' },
  allegrow: { title: 'Allegrow', hex: '44B6CA', src: '/brand-marks/allegrow.png' },
  'google-hotels': { title: 'Google Hotels', hex: '4285F4', src: '/brand-marks/google-hotels.svg' },
};
